<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Order Details" subTitle="Transactions" />

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <div v-else-if="!order" class="alert alert-danger">
      Order not found.
    </div>

    <div v-else>
      <div class="row">
        <!-- Main Content -->
        <div class="col-xxl-8 col-lg-7">
          
          <!-- Order Summary Card -->
          <div class="card bg-white border-0 rounded-3 mb-4">
            <div class="card-body py-4 px-0">
              <div class="default-table-area style-two all-projects">
                <div class="table-responsive">
                  <table class="table align-middle">
                    <thead>
                      <tr>
                        <th scope="col">Order ID</th>
                        <th scope="col">Customer</th>
                        <th scope="col">Service Type</th>
                        <th scope="col">Service Name</th>
                        <th scope="col">Klasifikasi Dokumen</th>
                        <th scope="col">Total Price</th>
                        <th scope="col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="text-body fw-bold">#{{ order.order_id }}</td>
                        <td>{{ order.customer?.name || order.customer?.phone_number || '-' }}</td>
                        <td><span class="badge bg-secondary">{{ order.service_type }}</span></td>
                        <td class="text-secondary">{{ order.service?.service_name || order.package?.package_name || '-' }}</td>
                        <td>
                          <span class="badge bg-primary-subtle text-primary fs-12 fw-medium">
                            {{ order.service_classification || 'Artikel Jurnal' }}
                          </span>
                          <small class="d-block text-muted" v-if="order.similarity_profile?.profile_name" style="font-size: 11px;">
                            {{ order.similarity_profile.profile_name }}
                          </small>
                        </td>
                        <td class="text-dark fw-bold">Rp {{ Number(order.total_price).toLocaleString('id-ID') }}</td>
                        <td>
                          <span class="badge p-2 fs-12 fw-normal" :class="statusClass(order.order_status)">
                            {{ order.order_status }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- 1. Detail Order: Rincian Kebutuhan Layanan -->
          <div class="card bg-white border-0 rounded-3 mb-4 shadow-sm">
            <div class="card-body p-4">
              <div class="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                <span class="material-symbols-outlined text-primary fs-22">assignment</span>
                <h4 class="fs-15 mb-0 fw-bold text-dark">Rincian Kebutuhan Layanan</h4>
              </div>

              <!-- Package Included Services Details (Independent of service_type) -->
              <div v-if="order.package" class="mb-4 pb-3 border-bottom">
                <h5 class="fs-14 fw-semibold mb-3">Services Included in Package ({{ order.package.package_name }})</h5>
                <div class="row">
                  <div class="col-md-6 col-lg-4 mb-3" v-for="svc in order.package.services" :key="svc.service_id">
                    <div class="card border border-light shadow-sm h-100 rounded-3 p-3">
                      <div class="d-flex align-items-center mb-2">
                        <span class="material-symbols-outlined text-primary fs-24 me-2">{{ svc.icon || 'settings' }}</span>
                        <h6 class="mb-0 fs-14 fw-semibold text-dark">{{ svc.service_name }}</h6>
                      </div>
                      <p class="text-secondary fs-13 mb-0 text-truncate-3" style="max-height: 54px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;">
                        {{ svc.description }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Plagiarism Details -->
              <div v-if="order.service_type === 'PLAGIARISM' && order.plagiarism_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.plagiarism_detail.language === 'id' ? 'Indonesian' : 'English' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Quotes</span>
                    <span class="fw-medium text-dark">{{ order.plagiarism_detail.exclude_quotes ? 'Yes' : 'No' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Bibliography</span>
                    <span class="fw-medium text-dark">{{ order.plagiarism_detail.exclude_bibliography ? 'Yes' : 'No' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Match</span>
                    <span class="fw-medium text-dark">{{ order.plagiarism_detail.exclude_match ? 'Yes' : 'No' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.plagiarism_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.plagiarism_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.plagiarism_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.plagiarism_detail.price_tier.label }}
                      <span v-if="order.plagiarism_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.plagiarism_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- AI Check Details -->
              <div v-else-if="order.service_type === 'AI_CHECK' && order.ai_check_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between" v-if="order.ai_check_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.ai_check_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.ai_check_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.ai_check_detail.price_tier.label }}
                      <span v-if="order.ai_check_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.ai_check_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- Paraphrase Details -->
              <div v-else-if="order.service_type === 'PARAPHRASE' && order.paraphrase_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.paraphrase_detail.language === 'id' ? 'Indonesian' : 'English' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Paraphrase Level</span>
                    <span class="fw-medium text-dark">{{ order.paraphrase_detail.paraphrase_level }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2">
                    <span class="text-secondary d-block mb-1">Special Instructions</span>
                    <div class="p-3 bg-light rounded">{{ order.paraphrase_detail.special_instructions || 'None provided.' }}</div>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.paraphrase_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.paraphrase_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.paraphrase_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.paraphrase_detail.price_tier.label }}
                      <span v-if="order.paraphrase_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.paraphrase_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- Doc Fix Details -->
              <div v-else-if="order.service_type === 'DOC_FIX' && order.document_fix_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.document_fix_detail.language === 'id' ? 'Indonesian' : 'English' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2">
                    <span class="text-secondary d-block mb-1">Fix Notes</span>
                    <div class="p-3 bg-light rounded">{{ order.document_fix_detail.fix_notes || 'None provided.' }}</div>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.document_fix_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.document_fix_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.document_fix_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.document_fix_detail.price_tier.label }}
                      <span v-if="order.document_fix_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.document_fix_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- Journal Details -->
              <div v-else-if="order.service_type === 'JOURNAL' && order.journal_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Target Index</span>
                    <span class="fw-medium text-dark">{{ order.journal_detail.target_index }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Field of Study</span>
                    <span class="fw-medium text-dark">{{ order.journal_detail.field_of_study }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.journal_detail.language === 'id' ? 'Indonesian' : 'English' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.journal_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.journal_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.journal_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.journal_detail.price_tier.label }}
                      <span v-if="order.journal_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.journal_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- Translate Details -->
              <div v-else-if="order.service_type === 'TRANSLATE' && order.translate_detail">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.translate_detail.language === 'id to en' ? 'Indonesia ke Inggris' : (order.translate_detail.language === 'en to id' ? 'Inggris ke Indonesia' : order.translate_detail.language) }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Field of Study</span>
                    <span class="fw-medium text-dark">{{ order.translate_detail.field_of_study }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.translate_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.translate_detail.page_count }} halaman</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.translate_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.translate_detail.price_tier.label }}
                      <span v-if="order.translate_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.translate_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- All Service Details -->
              <div v-else-if="order.service_type === 'ALL' && order.all_detail">
                <h5 class="fs-14 fw-semibold mb-3">Plagiarism Settings</h5>
                <ul class="list-group list-group-flush mb-4">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Language</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.language === 'id' ? 'Indonesian' : 'English' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Quotes</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.exclude_quotes ? 'Yes' : 'No' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Bibliography</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.exclude_bibliography ? 'Yes' : 'No' }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Exclude Match</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.exclude_match ? 'Yes' : 'No' }}</span>
                  </li>
                </ul>

                <h5 class="fs-14 fw-semibold mb-3">Paraphrase Settings</h5>
                <ul class="list-group list-group-flush mb-4">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Paraphrase Level</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.paraphrase_level }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2">
                    <span class="text-secondary d-block mb-1">Special Instructions</span>
                    <div class="p-3 bg-light rounded">{{ order.all_detail.special_instructions || 'None provided.' }}</div>
                  </li>
                </ul>

                <h5 class="fs-14 fw-semibold mb-3">Document Fix Settings</h5>
                <ul class="list-group list-group-flush mb-4">
                  <li class="list-group-item px-0 py-2">
                    <span class="text-secondary d-block mb-1">Fix Notes</span>
                    <div class="p-3 bg-light rounded">{{ order.all_detail.fix_notes || 'None provided.' }}</div>
                  </li>
                </ul>

                <h5 class="fs-14 fw-semibold mb-3">Journal Settings</h5>
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                    <span class="text-secondary">Target Index</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.target_index }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                    <span class="text-secondary">Field of Study</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.field_of_study }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.all_detail.page_count">
                    <span class="text-secondary">Page Count</span>
                    <span class="fw-medium text-dark">{{ order.all_detail.page_count }}</span>
                  </li>
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="order.all_detail.price_tier">
                    <span class="text-secondary">Price Tier</span>
                    <span class="fw-medium text-dark">
                      {{ order.all_detail.price_tier.label }}
                      <span v-if="order.all_detail.price_tier.deadline" class="badge bg-warning-subtle text-warning-emphasis ms-2">{{ order.all_detail.price_tier.deadline }}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <!-- Metadata (Processed By) -->
              <div class="mt-4 pt-3 border-top" v-if="order.processed_by">
                <ul class="list-group list-group-flush mb-0">
                  <li class="list-group-item px-0 py-2 d-flex justify-content-between border-0">
                    <span class="text-secondary">Last Processed By</span>
                    <span class="fw-medium text-dark">
                      {{ order.processed_by.name }} 
                      <small class="text-muted">({{ order.processed_by.email }})</small>
                    </span>
                  </li>
                </ul>
              </div>

            </div>
          </div>

          <!-- 2. Pilihan Klasifikasi Dokumen dan Status -->
          <div class="card bg-white border-0 rounded-3 mb-4 shadow-sm">
            <div class="card-header bg-light-subtle d-flex align-items-center gap-2 py-3 px-4">
              <span class="material-symbols-outlined text-primary fs-22">tune</span>
              <h5 class="mb-0 fs-15 fw-bold text-dark">Pilihan Klasifikasi Dokumen & Status</h5>
            </div>
            <div class="card-body p-4">
              <div class="row g-3 align-items-center">
                <div class="col-md-6">
                  <label class="form-label fs-13 fw-semibold text-secondary mb-1">
                    Klasifikasi / Preset Similarity Profile:
                  </label>
                  <div class="input-group">
                    <span class="input-group-text bg-light text-primary">
                      <span class="material-symbols-outlined fs-18">category</span>
                    </span>
                    <select class="form-select form-select-sm" v-model="profileUpdate" @change="updateProfile" :disabled="updatingProfile">
                      <option :value="null">-- Custom / Bebas --</option>
                      <option v-for="p in similarityProfiles" :key="p.id" :value="p.id">
                        {{ p.service_classification }} ({{ p.profile_name }})
                      </option>
                    </select>
                  </div>
                  <div class="mt-2" v-if="selectedProfileInfo">
                    <span class="badge bg-primary-subtle text-primary fs-11 me-1">
                      Maks. Total: {{ selectedProfileInfo.default_max_overall }}%
                    </span>
                    <span class="badge bg-info-subtle text-info fs-11 me-1">
                      Maks. Tunggal: {{ selectedProfileInfo.default_max_single_source }}%
                    </span>
                    <span class="badge bg-light text-muted border fs-11" v-if="selectedProfileInfo.exclude_bibliography">
                      Excl. Biblio
                    </span>
                    <span class="badge bg-light text-muted border fs-11 ms-1" v-if="selectedProfileInfo.exclude_quotes">
                      Excl. Quotes
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label fs-13 fw-semibold text-secondary mb-1">
                    Status Pesanan:
                  </label>
                  <div class="input-group">
                    <span class="input-group-text bg-light text-secondary">
                      <span class="material-symbols-outlined fs-18">sync_alt</span>
                    </span>
                    <select class="form-select form-select-sm" v-model="statusUpdate" @change="updateStatus">
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div class="mt-2 fs-12 text-muted">
                    Status saat ini: 
                    <span class="badge p-1 px-2 fs-11 fw-normal" :class="statusClass(order.order_status)">
                      {{ order.order_status }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- All Service Protocol Tabs Switcher (if ALL) -->
          <div v-if="order.service_type === 'ALL'" class="card border-0 rounded-3 mb-3 bg-white p-3 shadow-sm">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span class="fw-bold fs-14 text-dark">Paket Lengkap (ALL) - Pilih Protokol SOP:</span>
              <div class="btn-group">
                <button type="button" class="btn btn-sm" :class="activeProtocolTab === 'SIM' ? 'btn-primary' : 'btn-outline-primary'" @click="activeProtocolTab = 'SIM'">
                  1. Similaritas (RC-SIM)
                </button>
                <button type="button" class="btn btn-sm" :class="activeProtocolTab === 'GRM' ? 'btn-primary' : 'btn-outline-primary'" @click="activeProtocolTab = 'GRM'">
                  2. Grammarly & Editing (RC-GRM)
                </button>
                <button type="button" class="btn btn-sm" :class="activeProtocolTab === 'AI' ? 'btn-primary' : 'btn-outline-primary'" @click="activeProtocolTab = 'AI'">
                  3. AI Detector (RC-AI)
                </button>
              </div>
            </div>
          </div>

          <!-- Official RBN Protocol Forms based on Service Type (Steps 3-7) -->
          <SimilarityProtocolForm v-if="order.service_type === 'PLAGIARISM' || (order.service_type === 'ALL' && activeProtocolTab === 'SIM')" :order="order" @refresh="() => fetchOrder(order.order_id)" />
          <GrammarlyProtocolForm v-if="order.service_type === 'PARAPHRASE' || (order.service_type === 'ALL' && activeProtocolTab === 'GRM')" :order="order" @refresh="() => fetchOrder(order.order_id)" />
          <AiDetectorProtocolForm v-if="order.service_type === 'AI_CHECK' || (order.service_type === 'ALL' && activeProtocolTab === 'AI')" :order="order" @refresh="() => fetchOrder(order.order_id)" />

          <!-- 8. Deliverables Resmi & QC Management -->
          <OrderDeliverablesAndQc :order="order" @refresh="() => fetchOrder(order.order_id)" />
          <!-- Document Preview -->
          <div class="card bg-white border-0 rounded-3 mb-4" v-if="previewFiles.length > 0">
            <div class="card-body p-4">
              <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <h4 class="fs-15 mb-0 pb-1">Document Preview</h4>
                
                <!-- Tab selector for previewing multiple files -->
                <div class="d-flex flex-wrap gap-1" v-if="previewFiles.length > 1">
                  <button 
                    v-for="(file, idx) in previewFiles" 
                    :key="idx"
                    class="btn btn-sm py-1 px-2 fs-12 fw-medium border-0" 
                    :class="activePreviewIdx === idx ? 'btn-primary text-white' : 'btn-light text-secondary'"
                    @click="activePreviewIdx = idx"
                  >
                    {{ file.label }}
                  </button>
                </div>
              </div>
              
              <div v-for="(file, idx) in previewFiles" :key="idx" v-show="activePreviewIdx === idx">
                <iframe v-if="file.type === 'pdf'" :src="file.url" width="100%" height="600px" style="border: 1px solid #eee; border-radius: 8px;"></iframe>
                
                <div v-else-if="file.type === 'docx'" style="height: 600px; border: 1px solid #eee; border-radius: 8px; overflow: hidden;">
                  <vue-office-docx :src="file.url" style="height: 100%" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar / Attachments -->
        <div class="col-xxl-4 col-lg-5">
          <div class="card bg-white border-0 rounded-3 mb-4 rounded-3">
            <div class="card-body p-4">
              <h3 class="mb-4">Attachments & Files</h3>

              <!-- Dynamically render files based on service type -->
              <template v-if="order.service_type === 'AI_CHECK' && order.ai_check_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Source File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.ai_check_detail.source_file)" :target="isPdf(order.ai_check_detail.source_file) ? '_blank' : null" :download="!isPdf(order.ai_check_detail.source_file)" class="p-0 border-0 bg-transparent" v-if="order.ai_check_detail.source_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.ai_check_detail.result_report_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Result Report</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.ai_check_detail.result_report_file)" :target="isPdf(order.ai_check_detail.result_report_file) ? '_blank' : null" :download="!isPdf(order.ai_check_detail.result_report_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.ai_check_detail.ai_score">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-percent-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-warning rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">AI Score</h4>
                      <span class="fs-14 fw-bold text-dark">{{ order.ai_check_detail.ai_score }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="order.service_type === 'PLAGIARISM' && order.plagiarism_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Source File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.plagiarism_detail.source_file)" :target="isPdf(order.plagiarism_detail.source_file) ? '_blank' : null" :download="!isPdf(order.plagiarism_detail.source_file)" class="p-0 border-0 bg-transparent" v-if="order.plagiarism_detail.source_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.plagiarism_detail.result_report_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Result Report</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.plagiarism_detail.result_report_file)" :target="isPdf(order.plagiarism_detail.result_report_file) ? '_blank' : null" :download="!isPdf(order.plagiarism_detail.result_report_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.plagiarism_detail.plagiarism_ai_score">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-percent-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-warning rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Plagiarism Score</h4>
                      <span class="fs-14 fw-bold text-dark">{{ order.plagiarism_detail.plagiarism_ai_score }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="order.service_type === 'PARAPHRASE' && order.paraphrase_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Source File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.paraphrase_detail.source_file)" :target="isPdf(order.paraphrase_detail.source_file) ? '_blank' : null" :download="!isPdf(order.paraphrase_detail.source_file)" class="p-0 border-0 bg-transparent" v-if="order.paraphrase_detail.source_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.paraphrase_detail.result_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Completed File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.paraphrase_detail.result_file)" :target="isPdf(order.paraphrase_detail.result_file) ? '_blank' : null" :download="!isPdf(order.paraphrase_detail.result_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
              </template>

              <template v-if="order.service_type === 'DOC_FIX' && order.document_fix_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Raw File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.document_fix_detail.raw_file)" :target="isPdf(order.document_fix_detail.raw_file) ? '_blank' : null" :download="!isPdf(order.document_fix_detail.raw_file)" class="p-0 border-0 bg-transparent" v-if="order.document_fix_detail.raw_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.document_fix_detail.guideline_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-danger rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Guidelines</h4>
                      <span class="fs-13 text-secondary">Reference</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.document_fix_detail.guideline_file)" :target="isPdf(order.document_fix_detail.guideline_file) ? '_blank' : null" :download="!isPdf(order.document_fix_detail.guideline_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.document_fix_detail.final_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Fixed Document</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.document_fix_detail.final_file)" :target="isPdf(order.document_fix_detail.final_file) ? '_blank' : null" :download="!isPdf(order.document_fix_detail.final_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
              </template>

              <template v-if="order.service_type === 'JOURNAL' && order.journal_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-danger rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Manuscript</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.journal_detail.manuscript_file)" :target="isPdf(order.journal_detail.manuscript_file) ? '_blank' : null" :download="!isPdf(order.journal_detail.manuscript_file)" class="p-0 border-0 bg-transparent" v-if="order.journal_detail.manuscript_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.journal_detail.loi_certificate_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">LOI / Certificate</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.journal_detail.loi_certificate_file)" :target="isPdf(order.journal_detail.loi_certificate_file) ? '_blank' : null" :download="!isPdf(order.journal_detail.loi_certificate_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
              </template>

              <template v-if="order.service_type === 'TRANSLATE' && order.translate_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Source File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.translate_detail.manuscript_file)" :target="isPdf(order.translate_detail.manuscript_file) ? '_blank' : null" :download="!isPdf(order.translate_detail.manuscript_file)" class="p-0 border-0 bg-transparent" v-if="order.translate_detail.manuscript_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.translate_detail.result_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Translated File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.translate_detail.result_file)" :target="isPdf(order.translate_detail.result_file) ? '_blank' : null" :download="!isPdf(order.translate_detail.result_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
              </template>

              <!-- ALL service attachments -->
              <template v-if="order.service_type === 'ALL' && order.all_detail">
                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-primary rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Source File</h4>
                      <span class="fs-13 text-secondary">Document</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.source_file)" :target="isPdf(order.all_detail.source_file) ? '_blank' : null" :download="!isPdf(order.all_detail.source_file)" class="p-0 border-0 bg-transparent" v-if="order.all_detail.source_file">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.result_report_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Result Report</h4>
                      <span class="fs-13 text-secondary">Plagiarism Report</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.result_report_file)" :target="isPdf(order.all_detail.result_report_file) ? '_blank' : null" :download="!isPdf(order.all_detail.result_report_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.plagiarism_ai_score">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-percent-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-warning rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Plagiarism Score</h4>
                      <span class="fs-14 fw-bold text-dark">{{ order.all_detail.plagiarism_ai_score }}</span>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.result_file_paraphrase">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Completed File</h4>
                      <span class="fs-13 text-secondary">Paraphrase Result</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.result_file_paraphrase)" :target="isPdf(order.all_detail.result_file_paraphrase) ? '_blank' : null" :download="!isPdf(order.all_detail.result_file_paraphrase)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.guideline_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-danger rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Guidelines</h4>
                      <span class="fs-13 text-secondary">Doc Fix Guideline</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.guideline_file)" :target="isPdf(order.all_detail.guideline_file) ? '_blank' : null" :download="!isPdf(order.all_detail.guideline_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.final_file_doc_fix">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-word-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">Fixed Document</h4>
                      <span class="fs-13 text-secondary">Doc Fix Result</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.final_file_doc_fix)" :target="isPdf(order.all_detail.final_file_doc_fix) ? '_blank' : null" :download="!isPdf(order.all_detail.final_file_doc_fix)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>

                <div class="d-flex justify-content-between align-items-center bg-body-bg p-3 rounded-3 mb-3" v-if="order.all_detail.loi_certificate_file">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <i class="ri-file-pdf-line wh-40 lh-40 d-inline-block bg-white text-center fs-18 text-success rounded-circle"></i>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h4 class="mb-1 fs-14 fw-medium text-truncate" style="max-width: 180px;">LOI / Certificate</h4>
                      <span class="fs-13 text-secondary">Journal LOI</span>
                    </div>
                  </div>
                  <a :href="getFileUrl(order.all_detail.loi_certificate_file)" :target="isPdf(order.all_detail.loi_certificate_file) ? '_blank' : null" :download="!isPdf(order.all_detail.loi_certificate_file)" class="p-0 border-0 bg-transparent">
                    <i class="ri-download-2-line fs-22 text-primary"></i>
                  </a>
                </div>
              </template>

              <hr class="my-4" />

              <h4 class="fs-14 fw-medium mb-3">Upload Admin Result</h4>
              <div class="alert alert-info py-2 fs-13 mb-3">
                Upload final results/reports to deliver to the customer.
              </div>
              <form @submit.prevent="uploadAdminResult">
                <div class="mb-3" v-if="order.service_type === 'ALL'">
                  <label class="form-label fs-13 text-dark fw-medium">Upload File Target</label>
                  <select class="form-select form-select-sm" v-model="allResultType">
                    <option value="plagiarism">Plagiarism Report & Score</option>
                    <option value="paraphrase">Paraphrase Result File</option>
                    <option value="doc_fix">Fixed Document File</option>
                    <option value="journal">LOI / Certificate File</option>
                  </select>
                </div>
                <div class="mb-3" v-if="order.service_type === 'PLAGIARISM' || order.service_type === 'AI_CHECK' || (order.service_type === 'ALL' && allResultType === 'plagiarism')">
                  <label class="form-label fs-13">{{ order.service_type === 'AI_CHECK' ? 'AI Score' : 'Plagiarism Score' }} (e.g., 8%)</label>
                  <input type="text" class="form-control form-control-sm" v-model="plagiarismScore" :placeholder="order.service_type === 'AI_CHECK' ? 'AI Score' : 'Score'">
                </div>
                <div class="mb-3">
                  <input class="form-control form-control-sm" type="file" id="resultFile" @change="onFileChange" accept=".pdf,.doc,.docx,.zip,.rar">
                </div>
                <button class="btn btn-primary btn-sm w-100" :disabled="uploadingResult">
                  <span v-if="uploadingResult" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Upload Result
                </button>
              </form>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import api from '@/services/api';
import orderService from '@/services/orderService';
import uploadService from '@/services/uploadService';
import VueOfficeDocx from '@vue-office/docx';
import '@vue-office/docx/lib/index.css';
import OrderDeliverablesAndQc from './components/OrderDeliverablesAndQc.vue';
import SimilarityProtocolForm from './components/SimilarityProtocolForm.vue';
import GrammarlyProtocolForm from './components/GrammarlyProtocolForm.vue';
import AiDetectorProtocolForm from './components/AiDetectorProtocolForm.vue';

export default defineComponent({
  name: 'OrderDetailPage',
  components: {
    PageTitle,
    VueOfficeDocx,
    OrderDeliverablesAndQc,
    SimilarityProtocolForm,
    GrammarlyProtocolForm,
    AiDetectorProtocolForm
  },
  setup() {
    const route = useRoute();
    const order = ref<any>(null);
    const loading = ref(true);
    const statusUpdate = ref('');
    const similarityProfiles = ref<any[]>([]);
    const profileUpdate = ref<number | null>(null);
    const updatingProfile = ref(false);
    const activeProtocolTab = ref('SIM');
    const uploadingResult = ref(false);
    const resultFile = ref<File | null>(null);
    const plagiarismScore = ref('');
    const allResultType = ref('plagiarism');
    const activePreviewIdx = ref(0);

    const selectedProfileInfo = computed(() => {
      if (!profileUpdate.value) return null;
      return similarityProfiles.value.find(p => p.id === profileUpdate.value) || null;
    });

    const loadProfiles = async () => {
      try {
        const res = await api.get('/similarity-profiles');
        similarityProfiles.value = res.data.data || [];
      } catch (err) {
        console.error('Failed to load similarity profiles:', err);
      }
    };

    const fetchOrder = async (id: number) => {
      loading.value = true;
      try {
        const response = await orderService.getById(id);
        order.value = response.data;
        statusUpdate.value = order.value.order_status;
        profileUpdate.value = order.value.similarity_profile_id || (order.value.similarity_profile?.id ?? null);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        loading.value = false;
      }
    };

    const updateProfile = async () => {
      if (!order.value) return;
      updatingProfile.value = true;
      try {
        const matched = similarityProfiles.value.find(p => p.id === profileUpdate.value);
        await orderService.update(order.value.order_id, {
          similarity_profile_id: profileUpdate.value,
          service_classification: matched ? matched.service_classification : order.value.service_classification
        });
        alert('Klasifikasi / Profil dokumen berhasil diperbarui.');
        await fetchOrder(order.value.order_id);
      } catch (error) {
        console.error('Error updating document profile:', error);
        alert('Gagal memperbarui profil dokumen.');
        profileUpdate.value = order.value.similarity_profile_id;
      } finally {
        updatingProfile.value = false;
      }
    };

    const updateStatus = async () => {
      if (!order.value) return;

      if (statusUpdate.value === 'Completed') {
        const hasResult = !!(
          order.value.final_manuscript_docx ||
          order.value.raw_vendor_pdf ||
          order.value.plagiarism_detail?.result_report_file ||
          order.value.ai_check_detail?.result_report_file ||
          order.value.paraphrase_detail?.result_document_file ||
          order.value.document_fix_detail?.result_document_file ||
          order.value.journal_detail?.result_file ||
          order.value.translate_detail?.result_document_file ||
          order.value.all_detail?.loi_certificate_file
        );
        const hasProtocol = !!(
          order.value.generated_protocol_pdf ||
          order.value.verification_id ||
          order.value.protocol_id ||
          order.value.protocol_similarity ||
          order.value.protocol_grammarly ||
          order.value.protocol_ai_detector
        );

        if (!hasResult) {
          alert('Gagal menyelesaikan pesanan: Setidaknya 1 dokumen hasil (Naskah DOCX atau Laporan Asli Software) harus diunggah terlebih dahulu.');
          statusUpdate.value = order.value.order_status;
          return;
        }

        if (!hasProtocol) {
          alert('Gagal menyelesaikan pesanan: Protokol resmi harus digenerate setidaknya satu kali terlebih dahulu.');
          statusUpdate.value = order.value.order_status;
          return;
        }
      }

      try {
        await orderService.update(order.value.order_id, {
          order_status: statusUpdate.value
        });
        // refresh data or show success toast
        alert(`Order status updated to ${statusUpdate.value}`);
      } catch (error: any) {
        console.error('Error updating status', error);
        alert(error.response?.data?.message || 'Gagal memperbarui status order.');
        // revert back on error
        statusUpdate.value = order.value.order_status;
      }
    };

    const statusClass = (status: string) => {
      switch (status) {
        case 'Pending': return 'bg-warning bg-opacity-10 text-warning';
        case 'Processing': return 'bg-primary bg-opacity-10 text-primary';
        case 'Completed': return 'bg-success bg-opacity-10 text-success';
        case 'Cancelled': return 'bg-danger bg-opacity-10 text-danger';
        default: return 'bg-secondary bg-opacity-10 text-secondary';
      }
    };

    const onFileChange = (e: any) => {
      if (e.target.files && e.target.files[0]) {
        resultFile.value = e.target.files[0];
      }
    };

    const uploadAdminResult = async () => {
      if (!order.value) return;

      const type = order.value.service_type;

      // Validation
      if (type === 'PLAGIARISM' || type === 'AI_CHECK' || (type === 'ALL' && allResultType.value === 'plagiarism')) {
        if (!resultFile.value && !plagiarismScore.value) {
          alert("Please select a file or enter a score.");
          return;
        }
      } else {
        if (!resultFile.value) {
          alert("Please select a file to upload.");
          return;
        }
      }

      uploadingResult.value = true;
      try {
        let filePath = null;
        if (resultFile.value) {
          const uploadRes = await uploadService.uploadFile(resultFile.value);
          filePath = uploadRes.data.path;
        }

        const payload: any = {
          order_status: 'Completed'
        };

        if (type === 'PLAGIARISM') {
          payload.plagiarism = {};
          if (filePath) payload.plagiarism.result_report_file = filePath;
          if (plagiarismScore.value) payload.plagiarism.plagiarism_ai_score = plagiarismScore.value;
        } else if (type === 'AI_CHECK') {
          payload.ai_check = {};
          if (filePath) payload.ai_check.result_report_file = filePath;
          if (plagiarismScore.value) payload.ai_check.ai_score = plagiarismScore.value;
        } else if (type === 'PARAPHRASE') {
          payload.paraphrase = { result_file: filePath };
        } else if (type === 'DOC_FIX') {
          payload.doc_fix = { final_file: filePath };
        } else if (type === 'JOURNAL') {
          payload.journal = { loi_certificate_file: filePath };
        } else if (type === 'TRANSLATE') {
          payload.translate = { result_file: filePath };
        } else if (type === 'ALL') {
          payload.all = {};
          if (allResultType.value === 'plagiarism') {
            if (filePath) payload.all.result_report_file = filePath;
            if (plagiarismScore.value) payload.all.plagiarism_ai_score = plagiarismScore.value;
          } else if (allResultType.value === 'paraphrase') {
            if (filePath) payload.all.result_file_paraphrase = filePath;
          } else if (allResultType.value === 'doc_fix') {
            if (filePath) payload.all.final_file_doc_fix = filePath;
          } else if (allResultType.value === 'journal') {
            if (filePath) payload.all.loi_certificate_file = filePath;
          }
        }

        await orderService.update(order.value.order_id, payload);
        alert("Result uploaded successfully! Order is now Completed.");
        
        resultFile.value = null;
        plagiarismScore.value = '';
        const fileInput = document.getElementById('resultFile') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        
        await fetchOrder(order.value.order_id);
      } catch (error) {
        console.error("Error uploading result:", error);
        alert("Failed to upload result.");
      } finally {
        uploadingResult.value = false;
      }
    };

    const isPdf = (url: string) => {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf');
    };

    const isDocx = (url: string) => {
      if (!url) return false;
      return url.toLowerCase().endsWith('.docx') || url.toLowerCase().endsWith('.doc');
    };

    const getFileUrl = (path: string) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${path}`;
    };

    const previewFiles = computed(() => {
      if (!order.value) return [];
      const type = order.value.service_type;
      const files: { label: string; url: string; type: 'pdf' | 'docx' }[] = [];

      const addFile = (label: string, url: string) => {
        if (!url) return;
        if (isPdf(url)) {
          files.push({ label, url: getFileUrl(url), type: 'pdf' });
        } else if (isDocx(url)) {
          files.push({ label, url: getFileUrl(url), type: 'docx' });
        }
      };

      if (type === 'PLAGIARISM') {
        if (order.value.plagiarism_detail?.source_file) {
          addFile('Source File', order.value.plagiarism_detail.source_file);
        }
        if (order.value.plagiarism_detail?.result_report_file) {
          addFile('Result Report', order.value.plagiarism_detail.result_report_file);
        }
      } else if (type === 'AI_CHECK') {
        if (order.value.ai_check_detail?.source_file) {
          addFile('Source File', order.value.ai_check_detail.source_file);
        }
        if (order.value.ai_check_detail?.result_report_file) {
          addFile('Result Report', order.value.ai_check_detail.result_report_file);
        }
      } else if (type === 'PARAPHRASE') {
        if (order.value.paraphrase_detail?.source_file) {
          addFile('Source File', order.value.paraphrase_detail.source_file);
        }
        if (order.value.paraphrase_detail?.result_file) {
          addFile('Completed File', order.value.paraphrase_detail.result_file);
        }
      } else if (type === 'DOC_FIX') {
        if (order.value.document_fix_detail?.raw_file) {
          addFile('Raw File', order.value.document_fix_detail.raw_file);
        }
        if (order.value.document_fix_detail?.guideline_file) {
          addFile('Guidelines', order.value.document_fix_detail.guideline_file);
        }
        if (order.value.document_fix_detail?.final_file) {
          addFile('Fixed Document', order.value.document_fix_detail.final_file);
        }
      } else if (type === 'JOURNAL') {
        if (order.value.journal_detail?.manuscript_file) {
          addFile('Manuscript', order.value.journal_detail.manuscript_file);
        }
        if (order.value.journal_detail?.loi_certificate_file) {
          addFile('LOI / Certificate', order.value.journal_detail.loi_certificate_file);
        }
      } else if (type === 'TRANSLATE') {
        if (order.value.translate_detail?.manuscript_file) {
          addFile('Source File', order.value.translate_detail.manuscript_file);
        }
        if (order.value.translate_detail?.result_file) {
          addFile('Translated File', order.value.translate_detail.result_file);
        }
      } else if (type === 'ALL') {
        if (order.value.all_detail?.source_file) {
          addFile('Source File', order.value.all_detail.source_file);
        }
        if (order.value.all_detail?.guideline_file) {
          addFile('Guidelines', order.value.all_detail.guideline_file);
        }
        if (order.value.all_detail?.result_report_file) {
          addFile('Plagiarism Report', order.value.all_detail.result_report_file);
        }
        if (order.value.all_detail?.result_file_paraphrase) {
          addFile('Paraphrase Result', order.value.all_detail.result_file_paraphrase);
        }
        if (order.value.all_detail?.final_file_doc_fix) {
          addFile('Fixed Document', order.value.all_detail.final_file_doc_fix);
        }
        if (order.value.all_detail?.loi_certificate_file) {
          addFile('LOI / Certificate', order.value.all_detail.loi_certificate_file);
        }
      }
      return files;
    });

    watch(previewFiles, (newVal) => {
      if (activePreviewIdx.value >= newVal.length) {
        activePreviewIdx.value = 0;
      }
    });

    onMounted(() => {
      loadProfiles();
      if (route.params.id) {
        fetchOrder(Number(route.params.id));
      }
    });

    return {
      order,
      loading,
      statusUpdate,
      similarityProfiles,
      profileUpdate,
      selectedProfileInfo,
      updatingProfile,
      updateProfile,
      uploadingResult,
      plagiarismScore,
      allResultType,
      updateStatus,
      statusClass,
      onFileChange,
      uploadAdminResult,
      isPdf,
      isDocx,
      getFileUrl,
      previewFiles,
      activePreviewIdx,
      activeProtocolTab,
      fetchOrder
    };
  }
});
</script>
